/**
 * The writers: a parsed target → a port the router drives. MIDI moves a param the way the hand does, through the hand's own write paths.
 *
 * | Target                       | write (per frame)                    | end (idle)            |
 * |------------------------------|--------------------------------------|-----------------------|
 * | effect param, not driven     | `onLivePropChange(v, path, true)`    | `settleLiveProp`      |
 * | object param                 | `holdObjectWrites`                   | `settleObjectWrites`  |
 * | source param                 | `holdSourceWrite`                    | `settleSourceWrites`  |
 * | driven param, runner         | `touchLoop(path, v, true)`           | `releaseLoop`         |
 * | driven param, not the runner | — no engine to touch: unresolved     |                       |
 * | crossfader                   | `startScrubTransition`, `scrubTransition` | `releaseScrub`   |
 * | tempo                        | `tempo.setBpm` / `tempo.setOffset`   |                       |
 * | autoplay setting             | held                                 | one dispatch          |
 * | preset range                 | `loadPreset` of the pressed number's preset, on press        |
 * | action                       | `canTrigger` + `triggerAction` on press                      |
 *
 * `begin` pushes the gesture's one history entry, as a slider's mousedown does; every later write skips history. A port is resolved at gesture start and kept for the gesture, so a selection change or a drive switched on mid-move does not split it.
 */
import { canTrigger, triggerAction } from '~/lib/actionsManager'
import { getEngine } from '~/lib/automation'
import { currentValue, releaseLoop, touchLoop } from '~/lib/automation/hooks'
import { defOf, isDriven } from '~/lib/automation/model'
import tempo, { MAX_BPM, MAX_OFFSET_MS, MIN_BPM } from '~/lib/clock/tempo'
import { onLivePropChange, settleLiveProp } from '~/lib/fieldChangeHandlers'
import { applyObjectWrite, holdObjectWrites, settleObjectWrites } from '~/lib/objectWrites'
import { parseLivePath, schemaForLivePath, sourceParamPath } from '~/lib/params'
import { holdSourceWrite, settleSourceWrites } from '~/lib/sourceWrites'
import transitionManager from '~/lib/transitionManager'
import { selectTargetSource } from '~/lib/videoSource'
import store from '~/store'
import { setLiveProperty, updateObject } from '~/store/live'
import { pushHistory } from '~/store/middleware/history'
import global_playback_schema from '~/schemas/global_playback_schema'
import { setFadeDuration, setHoldDuration, setInstantTransitions, setLoop } from '~/store/playback'
import { selectPlaylist, selectPresets } from '~/store/playback/selectors'
import {
	loadPreset,
	releaseScrub,
	scrubTransition,
	startScrubTransition,
	toggleTransitionPaused,
} from '~/store/playback/thunks'
import { CHANNELS, isColorSchema, readChannel, writeChannel } from './colorChannels'
import { isMappable } from './shape'

// A batch already owns the undo step; `pushHistory` throws inside one.
const pushGesture = () => {
	if (!store.getState().history.batch.isActive) store.dispatch(pushHistory)
}

/** The concrete `live` path and object ids a param target names now; `@selected` reads the selection, `@source` the fill target's source. */
export const resolveParamPath = (spec, state) => {
	if (spec.sourceKey) {
		const source = selectTargetSource(state)
		return source ? { path: sourceParamPath(source, spec.sourceKey), ids: [] } : null
	}
	if (!spec.selected) {
		const p = parseLivePath(spec.path)
		return p ? { path: spec.path, ids: p.scope === 'object' ? [p.id] : [] } : null
	}
	const ids = state.selection?.objectIds ?? []
	if (!ids.length) return null
	return { path: `objects.${ids[0]}.${spec.selected}`, ids }
}

const effectPort = (path, schema) => ({
	schema,
	read: () => currentValue(path),
	begin: pushGesture,
	write: v => onLivePropChange(v, path, true),
	end: () => settleLiveProp(path),
	set: v => onLivePropChange(v, path),
})

const drivenPort = (path, schema) => ({
	schema,
	read: () => currentValue(path),
	write: v => touchLoop(path, v, true),
	end: () => releaseLoop(path),
	set: v => touchLoop(path, v),
})

// Every id gets the same value.
const objectEdit = (p, ids, objects, v) => write => {
	for (const id of ids) {
		const obj = objects[id]
		if (obj && !applyObjectWrite(write, obj, objects, p.key, v)) write(updateObject({ id, [p.key]: v }))
	}
}

// A source param is shared by every fill on its source: frames draw there, the end lands once.
const sourcePort = (path, schema) => ({
	schema,
	read: () => currentValue(path),
	begin: pushGesture,
	write: v => holdSourceWrite(path, v),
	end: settleSourceWrites,
	set: v => store.dispatch(setLiveProperty([path, v])),
})

const objectPort = (path, schema, ids) => {
	const p = parseLivePath(path)
	// The pre-gesture objects: a held frame must replace the last, not compound on it.
	let objects = null
	return {
		schema,
		read: () => currentValue(path),
		begin: () => {
			pushGesture()
			objects = store.getState().live.objects
		},
		write: v => holdObjectWrites(objects, objectEdit(p, ids, objects, v)),
		end: () => settleObjectWrites(store.dispatch),
		set: v => objectEdit(p, ids, store.getState().live.objects, v)(store.dispatch),
	}
}

const scopePort = (path, schema, ids) => {
	const { scope } = parseLivePath(path)
	if (scope === 'effect') return effectPort(path, schema)
	return scope === 'source' ? sourcePort(path, schema) : objectPort(path, schema, ids)
}

// One HSV channel of a colour: every frame writes the gesture's starting colour with that channel moved, so the others never drift.
const channelPort = (channel, base) => {
	let from = null
	const start = () => (from = base.read())
	return {
		schema: CHANNELS[channel],
		read: () => readChannel(base.read(), channel),
		begin: () => {
			base.begin?.()
			start()
		},
		write: v => base.write(writeChannel(from ?? start(), channel, v)),
		end: () => {
			from = null
			base.end?.()
		},
		set: v => base.set(writeChannel(base.read(), channel, v)),
	}
}

// `any`: a remote's value arrives in the param's own units, so a colour or a string has a port too (R10); a controller needs a position to map.
const paramPort = (spec, any) => {
	const state = store.getState()
	const at = resolveParamPath(spec, state)
	if (!at) return null
	const { path, ids } = at
	const schema = schemaForLivePath(path, state.live.objects)
	const driven = isDriven(defOf(state.live.modulators, path))
	if (spec.channel)
		return isColorSchema(schema) && !driven ? channelPort(spec.channel, scopePort(path, schema, ids)) : null
	if (any ? !schema || schema.readOnly : !isMappable(schema)) return null
	if (driven) return getEngine() ? drivenPort(path, schema) : null
	return scopePort(path, schema, ids)
}

const tempoPorts = {
	bpm: {
		schema: Object.freeze({ type: 'number', minimum: MIN_BPM, maximum: MAX_BPM, step: 0.1 }),
		read: () => tempo.snapshot().bpm,
		write: v => tempo.setBpm(v),
	},
	offset: {
		schema: Object.freeze({ type: 'number', minimum: -MAX_OFFSET_MS, maximum: MAX_OFFSET_MS, step: 1 }),
		read: () => tempo.snapshot().offset,
		write: v => tempo.setOffset(v),
	},
}

const PLAYBACK_SETTERS = {
	hold_duration: setHoldDuration,
	fade_duration: setFadeDuration,
	loop: setLoop,
	instant: setInstantTransitions,
}
const PLAYBACK_SCHEMAS = {
	...global_playback_schema.properties,
	loop: Object.freeze({ type: 'boolean' }),
	instant: Object.freeze({ type: 'boolean' }),
}

// An autoplay setting has no transient carrier: a gesture's frames are held here and land in Redux once, at its end.
const playbackPort = key => {
	const setter = PLAYBACK_SETTERS[key]
	let pending
	return {
		schema: PLAYBACK_SCHEMAS[key],
		read: () => store.getState().playback[key],
		write: v => {
			pending = v
		},
		end: () => {
			if (pending !== undefined) store.dispatch(setter(pending))
			pending = undefined
		},
		set: v => store.dispatch(setter(v)),
	}
}

// The range covers the project's own presets; the examples are a group of their own. A load is gated like the preset actions.
const presetsPort = {
	fire: index => {
		if (index < 0 || index >= selectPresets(store.getState()).length || !canTrigger('next_preset')) return
		store.dispatch(loadPreset({ index, select: true }))
	},
}

const CROSSFADER_SCHEMA = Object.freeze({ type: 'number', minimum: 0, maximum: 1, step: 0.001 })

/**
 * The crossfader drives the preset fade as an A/B fader: whichever end it rests at is the preset showing, and travel toward the other end fades into the next one. The end it left from is remembered for the fade it opened, so a pause mid-fade (which settles it parked) picks up without a jump.
 *
 * ⚠️ The end it leaves from is where it RESTED (`fader.last`), never the frame's own value: frames coalesce, so a fast slam's first frame can already be past the middle and would read as leaving from the far end — opening the fade backwards.
 *
 * A fade already running when the fader moves is grabbed — frozen, then scrubbed to the fader.
 */
const fader = { opened: -1, flip: false, last: undefined }

const crossfaderPort = () => {
	let open = false
	// One fade per gesture: a load that cut instead of fading (textures not ready) must not let the next frame open the one after it.
	let tried = false
	const playback = () => store.getState().playback
	const owned = () => fader.opened >= 0 && playback().next_preset_index === fader.opened
	return {
		schema: CROSSFADER_SCHEMA,
		// The fader's own state machine decides what a position means; a takeover would refuse the first move.
		takeover: 'jump',
		// A hand crossfade lingers; a short idle would settle it parked at every hesitation.
		idleMs: 500,
		read: () => {
			if (!owned()) return fader.flip ? 1 : 0
			const t = transitionManager.getProgress()
			return fader.flip ? 1 - t : t
		},
		write: v => {
			const pb = playback()
			// Where the fader rested before this frame; the first move ever has nothing else to go by.
			const rest = fader.last ?? v
			fader.last = v
			if (!open) {
				open = true
				if (!owned()) {
					if (pb.next_preset_index >= 0) {
						// Someone else's fade: freeze it and take it over where it is.
						if (!pb.transition_paused) store.dispatch(toggleTransitionPaused(pb.next_preset_index))
						fader.opened = pb.next_preset_index
						fader.flip = false
					} else fader.opened = -1
				}
			}
			if (fader.opened < 0) {
				// Leaving an end: that end is the preset showing, the other is the next one.
				fader.flip = rest > 0.5
				const t = fader.flip ? 1 - v : v
				if (t <= CROSSFADER_SCHEMA.step || tried) return
				tried = true
				const next = pb.current_preset_index + 1
				if (next >= selectPlaylist(store.getState()).length) return
				store.dispatch(startScrubTransition(next))
				if (playback().next_preset_index !== next) return
				fader.opened = next
			}
			store.dispatch(scrubTransition(fader.flip ? 1 - v : v))
		},
		end: () => {
			open = false
			tried = false
			if (fader.opened >= 0) store.dispatch(releaseScrub(fader.opened))
			// Settled at an end: the next move starts a new fade from whichever end the fader now rests at.
			if (playback().next_preset_index < 0) fader.opened = -1
		},
	}
}

/** A target's value now, for feedback: `{ schema, value }`, or null when it has none to show. */
export const readTarget = spec => {
	if (spec?.kind === 'tempo') {
		const port = tempoPorts[spec.key]
		return port && { schema: port.schema, value: port.read() }
	}
	if (spec?.kind === 'playback')
		return { schema: PLAYBACK_SCHEMAS[spec.key], value: store.getState().playback[spec.key] }
	if (spec?.kind !== 'param') return null
	const state = store.getState()
	const at = resolveParamPath(spec, state)
	if (!at) return null
	const schema = schemaForLivePath(at.path, state.live.objects)
	if (spec.channel)
		return isColorSchema(schema)
			? { schema: CHANNELS[spec.channel], value: readChannel(currentValue(at.path), spec.channel) }
			: null
	return isMappable(schema) ? { schema, value: currentValue(at.path) } : null
}

/** `{ any }`: accept every writable schema, not only a mappable one. */
export const resolvePort = (spec, { any = false } = {}) => {
	switch (spec?.kind) {
		case 'action':
			return { fire: () => canTrigger(spec.name) && triggerAction(spec.name) }
		case 'param':
			return paramPort(spec, any)
		case 'crossfader':
			return crossfaderPort()
		case 'tempo':
			return tempoPorts[spec.key] ?? null
		case 'playback':
			return playbackPort(spec.key)
		case 'presets':
			return presetsPort
		default:
			return null
	}
}
